import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { Waveform, ArrowLeft, CreditCard, CheckCircle, Clock, XCircle } from "@phosphor-icons/react";

import { API } from "@/lib/apiBase";

const SubscriptionHistoryPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { navigate("/login"); return; }
    axios.get(`${API}/subscription/history`, { withCredentials: true })
      .then((r) => setHistory(r.data.history || []))
      .finally(() => setLoading(false));
  }, [user, navigate]);

  const formatAmount = (amount, currency) => {
    const cur = currency || "INR";
    if (cur === "USD") return `$${Number(amount).toFixed(2)}`;
    return `₹${Math.round(Number(amount))}`;
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    } catch { return iso; }
  };

  const statusStyle = (s) => {
    if (s === "completed" || s === "paid") return { label: "Completed", color: "#10B981", icon: <CheckCircle size={14} weight="fill" /> };
    if (s === "pending") return { label: "Pending", color: "#F59E0B", icon: <Clock size={14} /> };
    return { label: s || "Unknown", color: "#EF4444", icon: <XCircle size={14} /> };
  };

  return (
    <div className="min-h-screen" style={{ background: "#0A0A0C" }}>
      <header className="relative z-10 backdrop-blur-xl bg-[#0A0A0C]/70 border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 md:px-8 lg:px-12 py-5 flex items-center gap-4">
          <button onClick={() => navigate("/")} className="text-white/60 hover:text-white transition-colors" data-testid="sub-history-back"><ArrowLeft size={24} /></button>
          <div className="flex items-center gap-3">
            <Waveform size={28} weight="bold" style={{ color: "#F59E0B" }} />
            <span className="text-xl font-light tracking-tight text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Subscription History</span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-8 lg:px-12 py-12">
        <div className="mb-8">
          <h1 className="text-3xl text-white font-light mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Your Transactions</h1>
          <p className="text-sm" style={{ color: "#A1A1AA", fontFamily: "'Manrope', sans-serif" }}>
            Every plan you've purchased, synced directly from Razorpay. For your records.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-[#F59E0B] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : history.length === 0 ? (
          <div className="bg-[#141417] border border-white/5 rounded-2xl p-12 text-center">
            <CreditCard size={48} weight="thin" style={{ color: "#A1A1AA" }} className="mx-auto mb-4" />
            <h3 className="text-xl text-white mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>No transactions yet</h3>
            <p className="mb-6" style={{ color: "#A1A1AA", fontFamily: "'Manrope', sans-serif" }}>Pick a plan to start creating Telugu AI songs.</p>
            <button onClick={() => navigate("/pricing")} className="bg-[#F59E0B] text-black font-semibold rounded-full px-8 py-3 hover:bg-[#D97706] transition-all" data-testid="sub-history-view-plans">View Plans</button>
          </div>
        ) : (
          <div className="space-y-3">
            {history.map((t, i) => {
              const s = statusStyle(t.status);
              return (
                <div key={t.razorpay_payment_id || t.razorpay_order_id || i} className="bg-[#141417] border border-white/5 hover:border-white/15 transition-all rounded-2xl p-5" data-testid={`sub-history-row-${i}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-white font-medium" style={{ fontFamily: "'Outfit', sans-serif" }}>{t.plan_name} Plan</p>
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full" style={{ background: `${s.color}1A`, color: s.color }}>{s.icon} {s.label}</span>
                      </div>
                      <p className="text-xs" style={{ color: "#A1A1AA", fontFamily: "'Manrope', sans-serif" }}>
                        Purchased on {formatDate(t.purchased_at)}
                      </p>
                      {t.razorpay_payment_id && (
                        <p className="text-xs mt-1" style={{ color: "#A1A1AA", fontFamily: "'Manrope', sans-serif" }}>
                          Payment ID: <span className="text-white/70">{t.razorpay_payment_id}</span>
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-light text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>{formatAmount(t.amount, t.currency)}</p>
                      <p className="text-xs" style={{ color: "#A1A1AA" }}>{t.currency || "INR"}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default SubscriptionHistoryPage;
